import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const WorkspaceContext = createContext(null);
const LS = "roots_ws";

// Cascading scope: Company -> Site -> Contract, plus an optional Zone focus.
// When a parent changes, a now-invalid child falls back to the first valid option
// (or "All zones" for the zone focus).
function normalizeSel(sel, entities, sites, contracts, zones) {
  const s = { ...sel };
  if (!entities.find((e) => e.id === s.entityId)) s.entityId = entities[0]?.id || "";
  const vSites = s.entityId ? sites.filter((x) => x.entity_id === s.entityId) : sites;
  if (!vSites.find((x) => x.id === s.siteId)) s.siteId = vSites[0]?.id || "";
  const vContracts = contracts.filter(
    (c) => (!s.entityId || c.entity_id === s.entityId) && (!s.siteId || (c.site_ids || []).includes(s.siteId))
  );
  if (!vContracts.find((c) => c.id === s.contractId)) s.contractId = vContracts[0]?.id || "";
  const vZones = s.siteId ? zones.filter((z) => z.site_id === s.siteId) : [];
  if (s.zoneId && !vZones.find((z) => z.id === s.zoneId)) s.zoneId = "";
  return s;
}

export function WorkspaceProvider({ children }) {
  const { user } = useAuth();
  const [entities, setEntities] = useState([]);
  const [sites, setSites] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [zones, setZones] = useState([]);
  const [sel, setSel] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(LS)) || {};
    } catch {
      return {};
    }
  });
  const [loading, setLoading] = useState(true);

  const persist = (next) => {
    setSel(next);
    localStorage.setItem(LS, JSON.stringify(next));
  };

  const reload = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [e, s, c, z] = await Promise.all([
        api.get("/entities"),
        api.get("/sites"),
        api.get("/contracts"),
        api.get("/zones"),
      ]);
      setEntities(e.data);
      setSites(s.data);
      setContracts(c.data);
      setZones(z.data);
      setSel((prev) => {
        const next = normalizeSel(prev, e.data, s.data, c.data, z.data);
        localStorage.setItem(LS, JSON.stringify(next));
        return next;
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) reload();
  }, [user, reload]);

  const update = (patch) =>
    persist(normalizeSel({ ...sel, ...patch }, entities, sites, contracts, zones));

  const visibleSites = sel.entityId ? sites.filter((s) => s.entity_id === sel.entityId) : sites;
  const visibleContracts = contracts.filter(
    (c) => (!sel.entityId || c.entity_id === sel.entityId) && (!sel.siteId || (c.site_ids || []).includes(sel.siteId))
  );
  const visibleZones = sel.siteId ? zones.filter((z) => z.site_id === sel.siteId) : [];

  const value = {
    entities,
    sites,
    contracts,
    zones,
    visibleSites,
    visibleContracts,
    visibleZones,
    reload,
    loading,
    entityId: sel.entityId || "",
    siteId: sel.siteId || "",
    contractId: sel.contractId || "",
    zoneId: sel.zoneId || "",
    setEntityId: (id) => update({ entityId: id }),
    setSiteId: (id) => update({ siteId: id }),
    setContractId: (id) => update({ contractId: id }),
    setZoneId: (id) => update({ zoneId: id === "all" ? "" : id }),
    site: sites.find((s) => s.id === sel.siteId) || null,
    contract: contracts.find((c) => c.id === sel.contractId) || null,
    entity: entities.find((e) => e.id === sel.entityId) || null,
    zone: zones.find((z) => z.id === sel.zoneId) || null,
  };

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export const useWorkspace = () => useContext(WorkspaceContext);
